import { Grid, List, SlidersHorizontal } from "lucide-react";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@b2b/ui-components";

export interface ProductListHeaderProps {
  totalResults: number;
  currentPage: number;
  itemsPerPage: number;
  sortBy: string;
  onSortChange: (sort: string) => void;
  itemsPerPageOptions?: number[];
  onItemsPerPageChange?: (items: number) => void;
  currentView?: "grid" | "list";
  onViewChange?: (view: "grid" | "list") => void;
  onFilterToggle?: () => void;
}

export const ProductListHeader: React.FC<ProductListHeaderProps> = ({
  totalResults,
  currentPage,
  itemsPerPage,
  sortBy,
  onSortChange,
  itemsPerPageOptions = [20, 40, 60, 100],
  onItemsPerPageChange,
  currentView = "grid",
  onViewChange,
  onFilterToggle,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalResults);

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-gray-50 py-3 px-4 border-b border-gray-200">
      <div className="flex items-center gap-4 flex-wrap">
        <span className="text-sm text-gray-600">
          Showing {startItem}-{endItem} of {totalResults.toLocaleString()} products
        </span>

        {onFilterToggle && (
          <Button
            variant="outline"
            size="sm"
            onClick={onFilterToggle}
            className="md:hidden"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 hidden sm:inline">Sort by:</span>
          <Select value={sortBy} onValueChange={onSortChange}>
            <SelectTrigger className="w-[160px] sm:w-[180px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="relevance">Relevance</SelectItem>
              <SelectItem value="price-low">Price: Low to High</SelectItem>
              <SelectItem value="price-high">Price: High to Low</SelectItem>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="popular">Most Popular</SelectItem>
              <SelectItem value="rating">Best Rating</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {onItemsPerPageChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 hidden sm:inline">Show:</span>
            <Select
              value={itemsPerPage.toString()}
              onValueChange={(value) => onItemsPerPageChange(parseInt(value))}
            >
              <SelectTrigger className="w-[80px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {itemsPerPageOptions.map((option) => (
                  <SelectItem key={option} value={option.toString()}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {onViewChange && (
          <div className="hidden md:flex items-center border border-gray-300 rounded-md">
            <Button
              variant={currentView === "grid" ? "default" : "ghost"}
              size="sm"
              onClick={() => onViewChange("grid")}
              className="rounded-r-none h-9"
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={currentView === "list" ? "default" : "ghost"}
              size="sm"
              onClick={() => onViewChange("list")}
              className="rounded-l-none h-9"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
